import { Component, OnInit, Input, OnChanges, OnDestroy } from '@angular/core';
import { FormGroup, Validators, FormBuilder, FormArray } from '@angular/forms';

import { ProjectionRate } from '@classes/simulation/projection-rate';
import { Partition } from '@classes/simulation/partition';
import { FormatService } from '@services/format.service';
import { SimulationService } from '@services/simulation.service';
import { UiService } from '@services/ui.service';
import { ProjectionRateType } from '@enums/projection-rate-type.enum';
import { Subscription } from 'rxjs';
import { SimulationYear } from '@classes/simulation/simulation-year';

@Component({
    selector: 'app-evolution-parameter',
    templateUrl: './evolution-parameter.component.html',
    styleUrls: ['./evolution-parameter.component.scss']
})
export class EvolutionParameterComponent implements OnInit, OnChanges, OnDestroy {
    @Input() projectionRate: ProjectionRate;
    @Input() statisticalsRates: ProjectionRate[];
    @Input() macroRates: ProjectionRate[];
    @Input() globalRates: ProjectionRate[];
    @Input() partition: Partition;
    @Input() isMJP: boolean;
    @Input() isNullMJP: boolean;

    private simulationValSub: Subscription;
    private selectedPartSub: Subscription;

    paramForm: FormGroup;
    tempRate: number;

    ratesArray: {label: string, rates: ProjectionRate[]}[];

    constructor(
        private formatSrv: FormatService,
        private simulationSrv: SimulationService,
        private fb: FormBuilder,
        private uiSrv: UiService
    ) { }

    ngOnInit() {
        this.initSubscriptions();
        this.initForm();
    }

    ngOnChanges() {
        this.initForm();
    }

    ngOnDestroy() {
        if(this.simulationValSub) {
            this.simulationValSub.unsubscribe();
        }
        if (this.selectedPartSub) {
            this.selectedPartSub.unsubscribe();
        }
    }

    private initSubscriptions(): void {
        this.simulationValSub = this.simulationSrv.simulationValChanged
            .subscribe(simulationValues => {
                this.initForm();
            });

        this.selectedPartSub = this.simulationSrv.selectedPartitionChanged
            .subscribe(() => {
                this.tempRate = null;
            });
    }

    private initForm(): void {
        const years = this.partition.simulationValues.years.filter(y => !y.isRateNeutralized).map(y => y.year);
        const rateValue = this.formatSrv.precision(this.projectionRate.rate * 100, 2);
        this.ratesArray = [];

        if(this.macroRates && this.macroRates.length) {
            this.ratesArray.push({
                label: this.uiSrv.translate('partitions.projectionRate.dropdown.macro.shortLabel'),
                rates: this.macroRates
            });
        }
        if(this.statisticalsRates && this.statisticalsRates.length) {
            this.ratesArray.push({
                label: this.uiSrv.translate('partitions.projectionRate.dropdown.statistical.shortLabel'),
                rates: this.statisticalsRates
            });
        }
        if(this.globalRates && this.globalRates.length) {
            this.ratesArray.push({
                label: this.uiSrv.translate('partitions.projectionRate.dropdown.global.shortLabel'),
                rates: this.globalRates
            });
        }

        this.paramForm = this.fb.group({
            rate: [{value: rateValue, disabled: (rateValue === -100 && !this.isMJP) || this.isNullMJP}, [Validators.required, Validators.min(-100), Validators.max(100)]],
            rates: [this.projectionRate.id || ''],
            neutralize: [rateValue === -100 && !this.isMJP],
            years: [years],
            items: this.fb.array(this.partition.simulationValues.years.map(y => this.fb.group({
                indice: this.formatSrv.precision(y.indice / 0.01, 2)
            })))
        });

        this.observeFormChanges();
    }

    private observeFormChanges(): void {
        this.paramForm.controls.rate.valueChanges
        .subscribe(rate => {
            this.applyRate(rate);
        });

        this.paramForm.controls.rates.valueChanges
        .subscribe((selectedId: number) => {
            this.selectRate(selectedId);
        });

        this.paramForm.controls.neutralize.valueChanges
        .subscribe((check: boolean) => {
            this.neutralizeRate(check);
        });

        this.paramForm.controls.years.valueChanges
        .subscribe(years => {
            this.selectYears(years);
        });

        this.paramForm.controls.items.valueChanges
        .subscribe(items => {
            this.applyIndices(items.map(i => i.indice));
        });
    }

    private applyRate(rate: number): void {
        if (rate == null) {
            rate = 0;
        }

        if(rate !== this.tempRate && -100 <= rate && rate <= 100) {
            this.tempRate = rate;

            this.projectionRate = {
                rate: this.formatSrv.precision(rate * 0.01, 4),
                type: ProjectionRateType.custom
            };

            if (this.isMJP) {
                this.applyRateMjp();
            } else {
                this.simulationSrv.projectionRateChanged.next(this.projectionRate);
            }
        }
    }

    private applyRateMjp(): void {
        this.partition.simulationValues.years.forEach(y => {
            if (!y.isRateNeutralized) {
                y.indice = this.projectionRate.rate;
            }
        });
        this.simulationSrv.simulationValChanged.next(this.partition.simulationValues);
    }

    private selectRate(selectedId: number): void {
        this.ratesArray.map(rateItem => {
            const found = rateItem.rates.find(rate => rate.id === selectedId);
            if(found) {
                this.projectionRate = found;
            }
        });

        if (!this.isMJP) {
            // save complete rate object
            this.simulationSrv.projectionRateChanged.next(this.projectionRate);
        } else {
            this.applyRateMjp();
        }

        // update input with % format
        this.tempRate = this.formatSrv.precision(this.projectionRate.rate * 100, 2);
        this.paramForm.controls.rate.setValue(this.tempRate);
    }

    private neutralizeRate(check: boolean): void {
        if(check) {
            this.paramForm.controls.rate.disable();
            this.paramForm.controls.rate.setValue(-100);
            this.paramForm.controls.rates.setValue(null);
        } else {
            this.paramForm.controls.rate.enable();
            this.paramForm.controls.rate.setValue(0);
            this.paramForm.controls.rates.setValue(null);
        }
    }

    private selectYears(selectedYears: number[]): void {
        this.partition.simulationValues.years.forEach(year => {
            const isRateNeutralized = !selectedYears.includes(year.year);
            if (year.isRateNeutralized !== isRateNeutralized) {
                year.isAmountEdited = false;
                year.isRateNeutralized = isRateNeutralized;
                year.indice = year.isRateNeutralized ? 0 : this.projectionRate.rate;
            }
        });
        this.simulationSrv.simulationValChanged.next(this.partition.simulationValues);
    }

    public applyIndices(indices: number[]): void {
        if (indices.includes(null)) {
            return; // possible si encodage d'une valeur négative
        }
        this.partition.simulationValues.years.map((y, i) => {
            if (!y.isRateNeutralized) {
                y.indice = this.formatSrv.precision(indices[i] * 0.01, 4);
            }
        });
        this.simulationSrv.simulationValChanged.next(this.partition.simulationValues);
    }
}
